import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Wand2 } from 'lucide-react';

const TONE_OPTIONS = [
  'Professional',
  'Casual',
  'Witty',
  'Inspiring',
  'Educational',
  'Promotional',
  'Storytelling',
  'Urgent',
];

const GOAL_OPTIONS = ['Awareness', 'Engagement', 'Clicks / traffic', 'Conversions', 'Community'];

interface ChipGroupProps {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  disabled?: boolean;
}

function ChipGroup({ options, selected, onToggle, disabled }: ChipGroupProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(opt)}
            className={cn(
              'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
              active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export interface AiAssistantContext {
  tone: string[];
  goal: string[];
  audience: string;
  additionalContext: string;
}

interface AiAssistantPanelProps {
  value: AiAssistantContext;
  onChange: (ctx: AiAssistantContext) => void;
  disabled?: boolean;
  canGenerate?: boolean;
  handleGenerate: () => void;
}

export function AiAssistantPanel({
  value,
  onChange,
  disabled,
  canGenerate = true,
  handleGenerate,
}: AiAssistantPanelProps) {
  const toggle = (field: 'tone' | 'goal', item: string) => {
    const current = value[field];
    const next = current.includes(item) ? current.filter((x) => x !== item) : [...current, item];
    onChange({ ...value, [field]: next });
  };

  return (
    <div className="rounded-lg shadow-social-card bg-background p-4 space-y-4">
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tone</p>
        <ChipGroup
          options={TONE_OPTIONS}
          selected={value.tone}
          onToggle={(item) => toggle('tone', item)}
          disabled={disabled}
        />
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Goal</p>
        <ChipGroup
          options={GOAL_OPTIONS}
          selected={value.goal}
          onToggle={(item) => toggle('goal', item)}
          disabled={disabled}
        />
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Target audience{' '}
          <span className="normal-case font-normal text-muted-foreground/70">(optional)</span>
        </p>
        <Input
          value={value.audience}
          onChange={(e) => onChange({ ...value, audience: e.target.value })}
          placeholder="e.g. founders, marketers, Gen Z consumers…"
          disabled={disabled}
          className={cn(
            'text-sm h-8',
            'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            'h-[40px] px-3 py-3 !font-mono',
            'text-primary',
            'placeholder-[var(--input-placeholder)]',
          )}
        />
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Additional context{' '}
          <span className="normal-case font-normal text-muted-foreground/70">(optional)</span>
        </p>
        <Textarea
          value={value.additionalContext}
          onChange={(e) => onChange({ ...value, additionalContext: e.target.value })}
          placeholder="Describe the purpose of this post — e.g. announcing a product launch, sharing a case study, promoting a limited-time offer…"
          disabled={disabled}
          rows={3}
          className={cn(
            'resize-none',
            'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            'px-3 py-3 !font-mono',
            'text-primary',
            'placeholder-[var(--input-placeholder)]',
          )}
        />
      </div>
      <button
        type="button"
        onClick={handleGenerate}
        disabled={disabled || !canGenerate}
        title={!canGenerate ? 'Select at least one account first' : undefined}
        className={cn(
          'border border-border/50 rounded-md bg-background flex gap-1 items-center px-2 py-1 font-mono text-[10px] hover:border-border',
          disabled || !canGenerate ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <Wand2 className="w-4 h-4 bg-violet-700 text-secondary p-1 rounded-sm" />
        GENERATE
      </button>
    </div>
  );
}
